import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SlidersHorizontal, Columns2, X } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { useVersion, PREVIEW_TOOLS } from "./VersionContext";
import { mapPath } from "./routes";
import SideBySide from "./SideBySide";

// Vorschau Fläche unten links: Version wechseln, Ecken abrunden, beide Versionen nebeneinander.
// Nur in Vorschau und Netlify Test, nie in den Livefassungen. Platz für weitere Funktionen ist vorgesehen.

const T = {
    de: {
        open: "Vorschau Einstellungen", title: "Vorschau", version: "Version",
        v1: "Original neu gestaltet", v2: "Einfaches Deutsch", v3: "Leere Fläche", v4: "Arbeitsfassung", v5: "Landingpage Überbestände",
        rounded: "Abgerundete Ecken", sbs: "Nebeneinander anzeigen", close: "Schließen",
    },
    en: {
        open: "Preview settings", title: "Preview", version: "Version",
        v1: "Original redesigned", v2: "Plain language", v3: "Blank canvas", v4: "Working copy", v5: "Surplus landing page",
        rounded: "Rounded corners", sbs: "Show side by side", close: "Close",
    },
};

const PreviewDock = () => {
    const { version, setVersion, rounded, setRounded, embed } = useVersion();
    const { lang } = useLanguage();
    const L = T[lang === "en" ? "en" : "de"];
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [sbs, setSbs] = useState(false);
    const wrap = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
        const onKey = (e) => e.key === "Escape" && setOpen(false);
        document.addEventListener("pointerdown", onDown);
        document.addEventListener("keydown", onKey);
        return () => { document.removeEventListener("pointerdown", onDown); document.removeEventListener("keydown", onKey); };
    }, [open]);

    const choose = (v) => {
        if (v === version) return;
        const target = mapPath(pathname, v);
        setVersion(v);
        navigate(target);
    };

    useEffect(() => {
        if (!PREVIEW_TOOLS || embed) return undefined;
        document.documentElement.classList.add("has-pv");
        return () => document.documentElement.classList.remove("has-pv");
    }, [embed]);

    const closeSbs = useCallback((at) => {
        setSbs(false);
        // nach dem Schließen auf der Seite weitermachen, die in der aktuellen Version zuletzt offen war
        if (at && at[version]) navigate(at[version]);
    }, [navigate, version]);

    if (!PREVIEW_TOOLS || embed) return null;

    return (
        <>
            <div ref={wrap} className="pv-dock" data-testid="preview-dock">
                {open && (
                    <div className="pv-panel" role="dialog" aria-label={L.title}>
                        <div className="pv-panel-head">
                            <span>{L.title}</span>
                            <button type="button" className="pv-x" onClick={() => setOpen(false)} aria-label={L.close}><X size={14} /></button>
                        </div>
                        <p className="pv-label">{L.version}</p>
                        <div className="pv-seg" role="radiogroup" aria-label={L.version}>
                            {[["1", L.v1], ["2", L.v2], ["3", L.v3], ["4", L.v4], ["5", L.v5]].map(([v, name]) => (
                                <button key={v} type="button" role="radio" aria-checked={version === v} className={version === v ? "is-on" : ""} onClick={() => choose(v)} data-testid={`preview-version-${v}`}>
                                    <b>{v}</b><span>{name}</span>
                                </button>
                            ))}
                        </div>
                        <label className="pv-row">
                            <span>{L.rounded}</span>
                            <button type="button" role="switch" aria-checked={rounded} className={`pv-switch ${rounded ? "is-on" : ""}`} onClick={() => setRounded(!rounded)} data-testid="preview-rounded"><i /></button>
                        </label>
                        <button type="button" className="pv-action" onClick={() => { setOpen(false); setSbs(true); }} data-testid="preview-sbs">
                            <Columns2 size={15} /> {L.sbs}
                        </button>
                    </div>
                )}
                <button type="button" className={`pv-trigger ${open ? "is-open" : ""}`} onClick={() => setOpen((o) => !o)} aria-label={L.open} aria-expanded={open} data-testid="preview-trigger">
                    <SlidersHorizontal size={15} />
                    <span className="pv-trigger-v">{version}</span>
                </button>
            </div>
            {sbs && <SideBySide startPath={pathname} rounded={rounded} onClose={closeSbs} />}
        </>
    );
};

export default PreviewDock;
